import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getPopularArticles, getLatestArticles } from '@/mocks/articles';

const faqData = [
  {
    question: '什么是内容分区导航？',
    answer: '内容分区导航是将平台上的所有内容按照主题、类型等维度进行分类整理，用户可以通过分区快速定位到自己感兴趣的内容区域。我们目前设有科技数码、影视娱乐、生活百科、教育学习、健康养生五大核心分区，每个分区都有丰富的内容资源。',
  },
  {
    question: '如何快速找到我感兴趣的内容？',
    answer: '您可以通过首页的分类导航入口直接进入感兴趣的分区，也可以在顶部导航栏中点击对应的分类名称。每个分类页面都提供了该分类下的全部文章列表，支持按发布时间和热门程度浏览。此外，首页的精选推荐和热门文章区域也能帮助您快速发现优质内容。',
  },
  {
    question: '平台上的内容是如何分类的？',
    answer: '我们的内容分类体系基于用户需求和内容特征两个维度进行设计。首先，我们分析了大量用户的浏览偏好，确定了最受关注的五大内容领域；其次，根据每篇文章的主题、标签和核心信息，将其归入最匹配的分类。每篇文章可以包含多个标签，帮助用户更精准地定位内容。',
  },
  {
    question: '平台内容多久更新一次？',
    answer: '我们保持每日更新的频率，确保用户能够及时获取到最新、最优质的内容资源。每个分类板块都会有定期的内容补充和优化，同时我们会根据用户的反馈和阅读数据不断调整内容策略。',
  },
  {
    question: '如何给我们提供反馈或建议？',
    answer: '我们非常重视用户的意见和建议。您可以通过"关于我们"页面上的联系方式与我们取得联系，包括邮箱、电话等渠道。我们会在收到反馈后的2个工作日内给予回复。',
  },
  {
    question: '平台支持哪些设备访问？',
    answer: '我们的平台采用响应式设计，完美适配电脑、平板和手机等多种设备。无论您使用什么设备浏览，都能获得流畅、舒适的阅读体验。所有功能在移动端和桌面端保持一致。',
  },
];

export default function Home() {
  const popularArticles = getPopularArticles(6);
  const latestArticles = getLatestArticles(6);

  const colorMap: Record<string, string> = {
    accent: 'bg-accent-100 text-accent-900',
    primary: 'bg-primary-100 text-primary-900',
    secondary: 'bg-secondary-100 text-secondary-900',
  };

  return (
    <>
      <SEOHead
        title="国产内容分区导航平台 - 精选内容聚合与分类导航"
        description="国产内容分区导航平台为您整合科技数码、影视娱乐、生活百科、教育学习、健康养生等分类资源，提供内容分类指南及使用建议，帮助用户快速了解内容分区逻辑与特征，找到最适合自己的内容区域。"
        keywords="内容分区导航,内容分类指南,内容资源整合,分区内容推荐,内容区域导航,科技数码,影视娱乐,生活百科,教育学习,健康养生"
        canonicalUrl={window.location.origin + (__BASE_PATH__ || '') + '/'}
        ogImage="https://readdy.ai/api/search-image?query=Modern%20content%20navigation%20platform%20website%20interface%20with%20clean%20categories%20and%20article%20cards%2C%20warm%20minimalist%20design%2C%20organized%20layout%2C%20professional%20web%20design%20aesthetic&width=1200&height=630&seq=home-og&orientation=landscape"
      />
      <SchemaOrg
        type="WebSite"
        data={{
          name: '内容导航平台',
          url: window.location.origin + (__BASE_PATH__ || ''),
          description: '国产内容分区导航平台，提供精选内容聚合与分类导航服务',
          potentialAction: {
            '@type': 'SearchAction',
            'target': {
              '@type': 'EntryPoint',
              'urlTemplate': window.location.origin + (__BASE_PATH__ || '') + '/search?q={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqData.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full pt-16">
            <div className="relative h-[480px] md:h-[560px] w-full overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20with%20warm%20gradients%20of%20coral%20and%20amber%20tones%2C%20soft%20flowing%20shapes%20representing%20content%20organization%20and%20navigation%2C%20clean%20minimalist%20design%2C%20modern%20digital%20art%20style%2C%20elegant%20and%20professional%20atmosphere&width=1600&height=900&seq=home-hero-main&orientation=landscape"
                alt="内容导航平台首页背景"
                className="w-full h-full object-cover"
                title="内容导航平台 - 精选内容聚合"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center w-full">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 max-w-3xl" style={{ fontFamily: 'var(--font-heading)' }}>
                  发现精彩内容，从这里开始
                </h1>
                <p className="text-base md:text-lg text-white/85 max-w-2xl mb-8 leading-relaxed">
                  覆盖科技数码、影视娱乐、生活百科、教育学习、健康养生五大核心分区，为您提供全方位的内容分类导航与精选推荐
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/category/tech-digital"
                    className="px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    开始浏览
                  </Link>
                  <Link
                    to="/about"
                    className="px-6 py-3 bg-white/15 text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                内容分区导航
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                选择您感兴趣的内容分区，快速浏览该领域的精选文章和最新资讯
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 hover:border-primary-200 transition-all group cursor-pointer text-center"
                >
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${colorMap[cat.color] || 'bg-accent-100 text-accent-900'}`}>
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500">{cat.articleCount} 篇文章</p>
                </Link>
              ))}
            </div>
          </section>

          {/* Popular Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 bg-background-100/50">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  热门内容推荐
                </h2>
                <p className="text-sm text-foreground-600">阅读量最高的精选文章</p>
              </div>
              <Link
                to="/category/tech-digital"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {popularArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </section>

          {/* Latest Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新发布
                </h2>
                <p className="text-sm text-foreground-600">最新上线的内容，等您来读</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </section>

          {/* Stats Section */}
          <section className="bg-background-100/70 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-accent-500 mb-1">5+</div>
                  <p className="text-sm text-foreground-600">核心内容分区</p>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">90+</div>
                  <p className="text-sm text-foreground-600">精选文章</p>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-secondary-500 mb-1">50万+</div>
                  <p className="text-sm text-foreground-600">月活跃用户</p>
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-accent-500 mb-1">99.9%</div>
                  <p className="text-sm text-foreground-600">服务可用率</p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">关于内容分区导航的常见疑问解答</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqData.map((item, index) => (
                <details key={index} className="group bg-background-50 rounded-lg border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-950 pr-4">{item.question}</span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="bg-primary-500 rounded-lg p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                准备好探索更多精彩内容了吗？
              </h2>
              <p className="text-white/85 text-sm md:text-base mb-8 max-w-xl mx-auto">
                立即选择您感兴趣的分区，开启内容探索之旅
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {categories.slice(0, 3).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="px-5 py-2.5 bg-white text-primary-500 rounded-full font-medium text-sm hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}